import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError("Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <img
              src="https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg"
              alt="Axentia Global"
              className="h-8 mb-2"
              data-testid="login-logo"
            />
            <div className="text-xs text-slate-500 font-body tracking-wider uppercase">
              Executive Search Academy
            </div>
          </div>

          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
              Sign In
            </h1>
            <p className="text-sm text-slate-500 mt-2 font-body">
              Internal platform. Authorized access only.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                Username
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="h-11 rounded-sm border-slate-200 focus-visible:ring-slate-900"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-11 rounded-sm border-slate-200 focus-visible:ring-slate-900"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error-message"
                className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-medium transition-all duration-200"
            >
              {loading ? (
                "Signing in..."
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
            <Lock className="w-3 h-3" />
            <span>Secured internal platform. Not for public access.</span>
          </div>
        </div>
      </div>

      {/* Right — Visual */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800" />
        <div className="relative z-10 px-16 text-center">
          <div className="mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg"
              alt="Axentia Global"
              className="h-12 mx-auto brightness-0 invert"
            />
          </div>
          <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-4">
            Executive Search Excellence
          </h2>
          <p className="text-slate-400 text-base leading-relaxed max-w-md mx-auto font-body">
            The complete learning and execution platform for building
            world-class executive search capability.
          </p>
          <div className="mt-10 flex items-center justify-center gap-8 text-sm text-slate-500">
            <div className="text-center">
              <div className="text-2xl font-bold text-white font-heading">20</div>
              <div className="mt-1">Modules</div>
            </div>
            <div className="w-px h-10 bg-slate-700" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white font-heading">50+</div>
              <div className="mt-1">Templates</div>
            </div>
            <div className="w-px h-10 bg-slate-700" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white font-heading">1</div>
              <div className="mt-1">Certification</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
